import { React, useState, useEffect, useRef } from 'react'
import { Box, Icon, Typography } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import AdxInput from 'components/custom/AdxInput'
import { InputAdornment } from '@mui/material'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postTaxData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const residentStatusDropdown = [
  {
    id: 1,
    description: 'Resident',
  },
  {
    id: 2,
    description: 'Non-Resident',
  },
  {
    id: 3,
    description: 'Returning Expert Program (REP)',
  },
  {
    id: 4,
    description: 'Knowledge Worker (IRDA)',
  },
  {
    id: 5,
    description: 'C suites',
  },
]

const TaxDetails = ({ data, employeeID, windowCheckEmployeeFile }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [disable, setDisable] = useState(true)

  const [initialValues, setInitialValues] = useState({
    resident_status: '',
    income_tax_number: '',
    previous_mtd: '',
    epf_contribution: '',
    gross_remuneration: '',
  })

  const validationSchema = Yup.object().shape({
    resident_status: Yup.number().nullable().required('Please select a status'),
    income_tax_number: Yup.string()
      .nullable()
      .required('Income Tax Number is required')
      .matches(/^[a-z\d\s]+$/i, 'Income Tax Number must be alphanumeric'),
    previous_mtd: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Previous Months MTD must be valid, eg. RM XXX.XX'
      ),
    epf_contribution: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Accumulated Months EPF Contribution must be valid, eg. RM XXX.XX'
      ),
    gross_remuneration: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Accumulated Months Gross Remuneration must be valid, eg. RM XXX.XX'
      ),
  })

  const mutation = useMutation((value) => postTaxData(employeeID, value), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeeTaxData', employeeID])
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitTax = (values, action) => {
    mutation.mutate(values)
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  useEffect(() => {
    if (data) {
      setInitialValues({
        resident_status: data?.data?.resident_status,
        income_tax_number: data?.data?.income_tax_number,
        previous_mtd: data?.data?.previous_mtd,
        epf_contribution: data?.data?.epf_contribution,
        gross_remuneration: data?.data?.gross_remuneration,
      })
    }
  }, [data])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <>
      <Formik
        onSubmit={handleSubmitTax}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        innerRef={formikRef}
      >
        {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, resetForm }) => (
          <Form>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                alignItems: 'center',
                borderColor: 'gray-line.main',
                p: 2,
              }}
            >
              <Typography color="light-gray-text.main" fontWeight={500}>
                Tax details
              </Typography>
            </Box>
            <Box
              p={2}
              borderBottom={windowCheckEmployeeFile && '1px solid'}
              sx={{
                borderColor: 'gray-line.main',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box gap={1} display="flex" justifyContent="space-between" alignItems="flex-end">
                <AdxDropdown
                  label="Tax Residency Status"
                  name="resident_status"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Salary category"
                  value={values.resident_status}
                  onChange={handleChange}
                  sx={{ width: 1 }}
                  errorMessage={<ErrorMessage name="resident_status" component={AdxErrorMessage} />}
                  isErrorShown={touched.resident_status && errors.resident_status}
                >
                  {residentStatusDropdown.map((status) => (
                    <AdxMenuItem value={status.id} selectedvalue={values.resident_status}>
                      {status.description}
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>
              <Box gap={2.5} display="flex" justifyContent="space-between">
                <AdxInput
                  label="Income Tax Number"
                  name="income_tax_number"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Income Tax Number"
                  value={values.income_tax_number}
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="income_tax_number" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.income_tax_number && errors.income_tax_number}
                  sx={{ width: 1 }}
                />
              </Box>
              <Box gap={1} display="flex">
                <AdxInput
                  label="Previous Months MTD"
                  name="previous_mtd"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Income Tax Number"
                  value={values.previous_mtd}
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="previous_mtd" component={AdxErrorMessage} />}
                  isErrorShown={touched.previous_mtd && errors.previous_mtd}
                  startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                  sx={{ width: 1 }}
                />
              </Box>
              <Box gap={2} display="flex">
                <AdxInput
                  label="Accumulated Months EPF Contribution"
                  name="epf_contribution"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Accumulated Months EPF Contribution"
                  value={values.epf_contribution}
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="epf_contribution" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.epf_contribution && errors.epf_contribution}
                  startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                  sx={{ width: 1 }}
                />
              </Box>
              <Box gap={2} display="flex">
                <AdxInput
                  label="Accumulated Months Gross Remuneration"
                  name="gross_remuneration"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Accumulated Months Gross Remuneration"
                  value={values.gross_remuneration}
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="gross_remuneration" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.gross_remuneration && errors.gross_remuneration}
                  startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                  sx={{ width: 1 }}
                />
              </Box>
              {windowCheckEmployeeFile && !disable && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={() => cancelEditForm(resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={() => handleCheckRef(handleSubmit)}
                    sx={{ width: 0.2 }}
                  >
                    Save
                  </AdxButton>
                </Box>
              )}
            </Box>
            {windowCheckEmployeeFile && disable && (
              <Box sx={{ p: 0.5 }}>
                <AdxButton
                  variant={'ghost'}
                  sx={{
                    pl: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  onClick={() => setDisable(false)}
                >
                  <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                  <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                    Edit tax details
                  </Typography>
                </AdxButton>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default TaxDetails
